import styles from '~/components/documents/code.module.scss'
import { openDocReviewPopover } from '~/utils/eventUtils'
import { cm } from '~/utils/utils'
import { lexicalState } from './plugins/UpdateContentPlugin'

export class DocReviewManager {
  private blockMarkdown: string
  private lineButtons: HTMLButtonElement[] = []
  private domElement!: HTMLElement
  private createLineButton: boolean

  constructor(
    private _hash: string,
    blockMarkdown: string,
    createLineButton: boolean
  ) {
    this.blockMarkdown = blockMarkdown
    this.createLineButton = createLineButton
  }

  public setDomElement(domElement: HTMLElement) {
    this.domElement = domElement
    const totalLines = this.blockMarkdown.split('\n').length
    this.createLineButtons(totalLines)
  }

  private createLineButtons(totalLines: number) {
    if (this.lineButtons.length > 0 || this.createLineButton === false) {
      return
    }
    this.lineButtons = Array.from({ length: totalLines }, (_, index) => {
      const button = document.createElement('button')
      button.className = cm(
        'absolute w-5 h-5 flex items-center justify-center',
        'text-on-background opacity-0 text-on-background',
        'left-[-1rem] border-outline text-xs hover:text-gray-400',
        'scale-x-[-1]',
        styles.lineButton
      )
      button.innerHTML = `
          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <path d="M 3 6 L 3 26 L 12.585938 26 L 16 29.414063 L 19.414063 26 L 29 26 L 29 6 Z M 5 8 L 27 8 L 27 24 L 18.585938 24 L 16 26.585938 L 13.414063 24 L 5 24 Z M 9 11 L 9 13 L 23 13 L 23 11 Z M 9 15 L 9 17 L 23 17 L 23 15 Z M 9 19 L 9 21 L 19 21 L 19 19 Z">
            </path>
          </svg>
        `.trim()
      button.style.top = `${index * 1.24}rem`

      button.addEventListener('mousedown', e => e.preventDefault())
      button.addEventListener('click', e => this.handleLineButtonClick(e, index))

      this.domElement.appendChild(button)
      return button
    })
  }

  handleMouseMove(e: MouseEvent) {
    const lineHeight = 19.89
    const rect = this.domElement.getBoundingClientRect()
    const relativeY = e.clientY - rect.top
    const lineIndex = Math.floor(relativeY / lineHeight)

    this.hideAllButtons()
    if (this.lineButtons[lineIndex]) {
      this.lineButtons[lineIndex].style.opacity = '1'
    }

    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      if (!range.collapsed) {
        const startLine = this.getLineNumberFromNode(range.startContainer)
        const endLine = this.getLineNumberFromNode(range.endContainer)
        if (startLine !== -1 && endLine !== -1) {
          this.showButtonsForLines(Math.min(startLine, endLine), Math.max(startLine, endLine))
        }
      }
    }
  }

  private showButtonsForLines(startLine: number, endLine: number) {
    this.hideAllButtons()
    if (this.lineButtons[startLine]) {
      this.lineButtons[startLine].style.opacity = '1'
    }
  }

  hideAllButtons() {
    this.lineButtons.forEach(button => {
      button.style.opacity = '0'
    })
  }

  private handleLineButtonClick(e: MouseEvent, lineIndex: number) {
    const selection = window.getSelection()
    if (!selection) return

    let startLine = lineIndex
    let endLine = lineIndex

    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      if (!range.collapsed) {
        startLine = this.getLineNumberFromNode(range.startContainer)
        endLine = this.getLineNumberFromNode(range.endContainer)
        if (startLine > endLine) {
          ;[startLine, endLine] = [endLine, startLine]
        }
      } else {
        startLine = endLine = lineIndex
      }
    }

    this.addCommentToLines(e, startLine, endLine)

    e.stopPropagation()
  }

  private getLineNumberFromNode(node: Node): number {
    let currentNode: Node | null = node
    let lineCount = 0

    while (currentNode && currentNode !== this.domElement) {
      while (currentNode.previousSibling) {
        currentNode = currentNode.previousSibling
        if (currentNode.nodeName === 'BR') {
          lineCount++
        }
      }
      currentNode = currentNode.parentNode
    }

    return lineCount
  }

  private async addCommentToLines(event: MouseEvent, startLine: number, endLine: number) {
    const blockStart = lexicalState.fullMarkdown.indexOf(this.blockMarkdown)
    const linesBefore = lexicalState.fullMarkdown.slice(0, blockStart).split('\n').length - 1

    const absoluteStartLine = linesBefore + startLine + 1
    const absoluteEndLine = linesBefore + endLine + 1

    this.highlightSelectedLines(startLine, endLine)

    await openDocReviewPopover({
      clientX: event.clientX,
      clientY: event.clientY,
      startLine: absoluteStartLine,
      endLine: absoluteEndLine
    })
    this.removeHighlights()
  }

  private highlightSelectedLines(startLine: number, endLine: number) {
    this.removeHighlights()

    let currentLine = 0

    const highlightLine = (node: Node) => {
      if (node != null) {
        const element = node.parentElement as HTMLElement
        element.classList.add('bg-[#fef8c5]')
      }
    }

    const walkNodes = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent!.trim() !== '') {
        if (currentLine >= startLine && currentLine <= endLine) {
          highlightLine(node)
        }
      } else if (node.nodeName === 'BR') {
        currentLine++
      } else {
        for (const childNode of Array.from(node.childNodes)) {
          walkNodes(childNode)
        }
      }
    }

    walkNodes(this.domElement)
  }

  private removeHighlights() {
    const walkNodes = (node: Node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement
        element.classList.remove('bg-[#fef8c5]')
      }
      for (const childNode of Array.from(node.childNodes)) {
        walkNodes(childNode)
      }
    }
    walkNodes(this.domElement)
  }
}
